import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Building2, ChevronRight, Shield, History, Search, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  prospecting: "bg-zinc-100 text-zinc-700",
  approved: "bg-emerald-100 text-emerald-800",
  preferred: "bg-blue-100 text-blue-800",
  suspended: "bg-amber-100 text-amber-800",
  blacklisted: "bg-red-100 text-red-700",
};

export default function SupplierManagement() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    company_name: "", primary_contact_email: "", country: "",
    commodity_categories: "", primary_contact_name: "", address: "", phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [audits, setAudits] = useState([]);
  const [blacklistReason, setBlacklistReason] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/suppliers")
      .then((r) => setSuppliers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectSupplier = async (s) => {
    setSelected(s);
    try {
      const r = await api.get(`/suppliers/${s.id}`);
      setDetail(r.data);
    } catch {
      setDetail(s);
    }
    // Load audit history
    api.get(`/suppliers/${s.id}/audits`).then((r) => setAudits(r.data)).catch(() => setAudits([]));
  };

  const create = async () => {
    setSaving(true);
    try {
      const categories = form.commodity_categories
        ? form.commodity_categories.split(",").map((c) => c.trim()).filter(Boolean)
        : [];
      await api.post("/suppliers", {
        company_name: form.company_name,
        primary_contact_email: form.primary_contact_email,
        primary_contact_name: form.primary_contact_name,
        country: form.country,
        commodity_categories: categories,
        address: form.address,
        phone: form.phone,
      });
      toast.success("Supplier created");
      setShowCreate(false);
      setForm({ company_name: "", primary_contact_email: "", country: "", commodity_categories: "", primary_contact_name: "", address: "", phone: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/suppliers/${id}/status`, { status });
      toast.success(`Supplier marked as ${status}`);
      setSelected(null);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filteredSuppliers = suppliers.filter((s) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = (s.company_name || "").toLowerCase().includes(q);
      const matchCountry = (s.country || "").toLowerCase().includes(q);
      const matchCommodity = (s.commodity_categories || []).some((c) => c.toLowerCase().includes(q));
      if (!matchName && !matchCountry && !matchCommodity) return false;
    }
    if (statusFilter && s.status !== statusFilter) return false;
    if (countryFilter && s.country !== countryFilter) return false;
    return true;
  });

  const countries = [...new Set(suppliers.map((s) => s.country).filter(Boolean))].sort();

  const isCertExpiringSoon = (cert) => {
    if (!cert.expiry_date) return false;
    const expiry = new Date(cert.expiry_date);
    const now = new Date();
    const diffDays = (expiry - now) / (1000 * 60 * 60 * 24);
    return diffDays <= 90 && diffDays > 0;
  };

  const isCertExpired = (cert) => {
    if (!cert.expiry_date) return false;
    return new Date(cert.expiry_date) < new Date();
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Suppliers</h1>
          <p className="text-zinc-500 mt-2 text-sm">Manage suppliers, facilities, certifications and audit history.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> Add supplier
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, country, or commodity..."
            className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
          <option value="">All statuses</option>
          {["prospecting", "approved", "preferred", "suspended", "blacklisted"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}
          className="border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
          <option value="">All countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : filteredSuppliers.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <Building2 size={26} className="mx-auto mb-3" />
          <p className="text-sm">{suppliers.length === 0 ? "No suppliers yet." : "No suppliers match your filters."}</p>
        </div>
      ) : (
        <div className="mt-6 flex gap-6">
          {/* Supplier List */}
          <div className="flex-1 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-4">Supplier</span>
              <span className="col-span-2">Country</span>
              <span className="col-span-2">Commodities</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-2 text-right">Details</span>
            </div>
            {filteredSuppliers.map((s) => (
              <div key={s.id} className={`grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 ${selected?.id === s.id ? "bg-zinc-50" : ""}`}
                onClick={() => selectSupplier(s)}>
                <div className="col-span-4">
                  <p className="text-sm font-medium">{s.company_name}</p>
                  <p className="text-xs text-zinc-400">{s.primary_contact_email || s.primary_contact_name}</p>
                </div>
                <span className="col-span-2 text-sm text-zinc-600">{s.country}</span>
                <span className="col-span-2 text-xs text-zinc-500 truncate">{(s.commodity_categories || []).join(", ") || "—"}</span>
                <span className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[s.status] || STATUS_STYLE.prospecting}`}>
                    {s.status}
                  </span>
                </span>
                <span className="col-span-2 text-right"><ChevronRight size={14} className="inline text-zinc-400" /></span>
              </div>
            ))}
          </div>

          {/* Detail Panel */}
          {detail && (
            <div className="w-80 shrink-0 border border-zinc-200 bg-white rounded-lg p-6 max-h-[80vh] overflow-auto">
              <h3 className="font-heading font-semibold">{detail.company_name}</h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[detail.status] || STATUS_STYLE.prospecting}`}>
                {detail.status}
              </span>
              <div className="mt-4 space-y-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Contact</p>
                  <p className="text-sm">{detail.primary_contact_name || "N/A"}</p>
                  <p className="text-xs text-zinc-500">{detail.primary_contact_email || ""}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Country</p>
                  <p className="text-sm">{detail.country || "N/A"}</p>
                </div>
                {detail.address && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Address</p>
                    <p className="text-sm">{detail.address}</p>
                  </div>
                )}
                {(detail.commodity_categories || []).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Commodities</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detail.commodity_categories.map((c, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 bg-zinc-100 rounded-full">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Facilities & Certifications */}
              {(detail.facilities || []).length > 0 && (
                <div className="mt-5 pt-4 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1 mb-2"><Building2 size={10} /> Facilities</p>
                  {detail.facilities.map((f) => (
                    <div key={f.id} className="mb-3 p-3 border border-zinc-100 rounded-md">
                      <p className="text-sm font-medium">{f.name}</p>
                      <p className="text-xs text-zinc-400">{f.country} · {f.capacity_units_per_month ? `${f.capacity_units_per_month} units/mo` : ""}</p>
                      {f.certifications && f.certifications.map((cert) => (
                        <div key={cert.id} className={`mt-1 flex items-center gap-1.5 text-xs ${isCertExpired(cert) ? "text-red-600" : isCertExpiringSoon(cert) ? "text-amber-600" : "text-zinc-500"}`}>
                          {isCertExpired(cert) ? <AlertTriangle size={10} /> : isCertExpiringSoon(cert) ? <AlertTriangle size={10} /> : <Shield size={10} />}
                          {cert.cert_type}
                          {cert.expiry_date && (
                            <span className="text-[10px]">
                              {isCertExpired(cert) ? "(expired)" : isCertExpiringSoon(cert) ? "(expiring soon)" : `exp ${new Date(cert.expiry_date).toLocaleDateString()}`}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Audit History */}
              {audits.length > 0 && (
                <div className="mt-5 pt-4 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1 mb-2"><History size={10} /> Audit History</p>
                  <div className="space-y-2">
                    {audits.map((a) => (
                      <div key={a.id} className="bg-zinc-50 rounded-md p-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">{a.audit_type || "General Audit"}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${a.overall_result === "pass" ? "bg-emerald-100 text-emerald-800" : a.overall_result === "fail" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-600"}`}>
                            {a.overall_result || "pending"}
                          </span>
                        </div>
                        {a.auditor_name && <p className="text-[10px] text-zinc-400 mt-0.5">Auditor: {a.auditor_name}</p>}
                        {a.audit_date && <p className="text-[10px] text-zinc-400">{new Date(a.audit_date).toLocaleDateString()}</p>}
                        {(a.findings || []).length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {a.findings.slice(0, 3).map((f, i) => (
                              <p key={i} className="text-[10px] text-zinc-500">- {f.description || f}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blacklist Reason */}
              {detail.blacklist_reason && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-red-600 mb-1">Blacklist Reason</p>
                  <p className="text-xs text-red-700">{detail.blacklist_reason}</p>
                </div>
              )}

              {/* Cert Expiry Summary */}
              {(() => {
                const allCerts = (detail.facilities || []).flatMap((f) => (f.certifications || []).map((c) => ({ ...c, facility: f.name })));
                const expiredCerts = allCerts.filter((c) => isCertExpired(c));
                const expiringCerts = allCerts.filter((c) => isCertExpiringSoon(c));
                if (expiredCerts.length === 0 && expiringCerts.length === 0) return null;
                return (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 mb-1">Certificate Alerts</p>
                    {expiredCerts.map((c, i) => (
                      <p key={`exp-${i}`} className="text-[10px] text-red-600 flex items-center gap-1"><AlertTriangle size={10} /> {c.cert_type} ({c.facility}) — EXPIRED</p>
                    ))}
                    {expiringCerts.map((c, i) => (
                      <p key={`warn-${i}`} className="text-[10px] text-amber-700 flex items-center gap-1"><AlertTriangle size={10} /> {c.cert_type} ({c.facility}) — expires {new Date(c.expiry_date).toLocaleDateString()}</p>
                    ))}
                  </div>
                );
              })()}

              {/* Status Transitions */}
              <div className="mt-5 pt-4 border-t border-zinc-100 space-y-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Change Status</p>
                {["prospecting", "approved", "preferred", "suspended", "blacklisted"].filter((s) => s !== detail.status).map((s) => (
                  s === "blacklisted" ? (
                    <div key={s}>
                      <input value={blacklistReason} onChange={(e) => setBlacklistReason(e.target.value)} placeholder="Blacklist reason (required)"
                        className="w-full border border-zinc-300 rounded-md px-2.5 py-1.5 text-xs mb-1 focus:outline-none focus:ring-1 focus:ring-red-500" />
                      <button onClick={() => {
                        if (!blacklistReason.trim()) { toast.error("Reason is required for blacklisting"); return; }
                        api.patch(`/suppliers/${detail.id}/status`, { status: "blacklisted", blacklist_reason: blacklistReason })
                          .then(() => { toast.success("Supplier blacklisted"); setSelected(null); setDetail(null); setBlacklistReason(""); load(); })
                          .catch((e) => toast.error(apiError(e)));
                      }} disabled={!blacklistReason.trim()}
                        className={`w-full text-left px-3 py-2 rounded-md text-xs font-medium capitalize hover:opacity-80 disabled:opacity-40 ${STATUS_STYLE[s]}`}>
                        {s}
                      </button>
                    </div>
                  ) : (
                    <button key={s} onClick={() => updateStatus(detail.id, s)}
                      className={`w-full text-left px-3 py-2 rounded-md text-xs font-medium capitalize hover:opacity-80 ${STATUS_STYLE[s]}`}>
                      {s}
                    </button>
                  )
                ))}
                {/* Delete Supplier */}
                <button onClick={async () => {
                  if (!window.confirm("Delete this supplier permanently? This cannot be undone.")) return;
                  try {
                    await api.delete(`/suppliers/${detail.id}`);
                    toast.success("Supplier deleted");
                    setSelected(null);
                    setDetail(null);
                    load();
                  } catch (e) { toast.error(apiError(e)); }
                }}
                  className="w-full text-left px-3 py-2 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 mt-2">
                  Delete supplier
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Add supplier</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Company name *"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} placeholder="Primary contact name *"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} placeholder="Contact email *"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country *"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.commodity_categories} onChange={(e) => setForm({ ...form, commodity_categories: e.target.value })} placeholder="Commodity categories (comma-separated)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={create} disabled={!form.company_name || !form.primary_contact_name || !form.primary_contact_email || !form.country || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Adding..." : "Add supplier"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
